'use client';

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Trash2, Ban, Plus, AlertCircle, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { StringBadge } from '../../glitch-ui/string-badge';
import { ConformationModal } from '../../glitch-ui/confirmation-modal';
import { FieldError } from '@/components/glitch-ui/field-error';
import type { Ad, CampaignData } from '@/lib/types';
import type { InheritedNegativeKeyword } from '@/lib/apis';
import { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '../../glitch-ui/data-table';
import { MatchTypeSelect } from './match-type-select';

// Backend NegativeKeyword model fields
interface NegativeKeywordData {
  id?: number;
  keyword: string;
  text?: string;
  match_type?: string;
  is_paused?: boolean;
  created_at?: string;
  updated_at?: string;
  googleads_ad_group_id?: string;
}

// Unified table row: the ad group's own negatives (deletable) plus negatives inherited from the
// campaign and from applied account shared lists (read-only — managed at their source).
interface NegativeRow extends NegativeKeywordData {
  sourceLabel: string;
  sourceType: 'own' | 'shared_list' | 'campaign';
  deletable: boolean;
}

interface NegativeKeywordsManagerProps {
  campaign: CampaignData;
  selectedAd: Ad | null;
  disabled?: boolean;
  /** Hide the Add button while the ad is still generating. */
  hideAddButton?: boolean;
  onRefresh: () => Promise<void>;
  negativeKeywords?: NegativeKeywordData[];
  // Read-only negatives that apply to this ad group via the campaign / account shared lists.
  inheritedNegatives?: InheritedNegativeKeyword[];
  onMatchTypeChange?: (keyword: NegativeKeywordData, newMatchType: MatchType) => void;
  onRemove?: (keyword: NegativeKeywordData) => void;
  onAdd?: (keywords: NegativeKeywordData[]) => void;
}

// Helper to get keyword text from either field
const getKeywordText = (kw: NegativeKeywordData): string => {
  return kw.keyword || kw.text || '';
};

// Format date for display
const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '-';
  }
};

const getMatchTypePillStyle = (matchType: string) => {
  switch (matchType) {
    case 'EXACT':
      return 'bg-purple-50 text-purple-600';
    case 'PHRASE':
      return 'bg-blue-50 text-blue-600';
    case 'BROAD':
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

// Shared-list rows get the cobalt info pill so inherited negatives stand out from the level's own.
const getSourcePillStyle = (sourceType: NegativeRow['sourceType']) =>
  sourceType === 'shared_list' ? 'bg-[#EDE9FE] text-[#290BD9]' : 'bg-gray-100 text-gray-700';

const MATCH_TYPES = ['EXACT', 'PHRASE', 'BROAD'] as const;
type MatchType = (typeof MATCH_TYPES)[number];

const formatMatchType = (matchType: string): string => {
  return matchType.charAt(0) + matchType.slice(1).toLowerCase();
};

export function NegativeKeywordsManager({ campaign, selectedAd, disabled = false, hideAddButton = false, negativeKeywords: negativeKeywordsProp, inheritedNegatives, onMatchTypeChange, onRemove, onAdd }: NegativeKeywordsManagerProps) {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bulkKeywords, setBulkKeywords] = useState('');
  const [matchType, setMatchType] = useState<MatchType>('PHRASE');
  // Tell-on-click validation (#5857): Add stays enabled; clicking it marks the
  // textarea inline ('Required' / all-duplicates) instead of silently returning.
  const [bulkKeywordsError, setBulkKeywordsError] = useState<string | undefined>(undefined);

  // Use prop if provided (tracked by parent for dirty state), otherwise fall back to ad data
  const [deleteTarget, setDeleteTarget] = useState<NegativeKeywordData | null>(null);
  const negativeKeywords = negativeKeywordsProp || (selectedAd?.negative_keywords || []) as unknown as NegativeKeywordData[];

  const activeKeywords = negativeKeywords.filter(nk => !nk.is_paused);
  const pausedKeywords = negativeKeywords.filter(nk => nk.is_paused);

  // Badge counts every negative actively applied to the ad: the ad group's own non-paused
  // negatives plus all inherited campaign / shared-list rows (inherited rows are always active).
  // Paused own negatives stay out of the badge — they're surfaced separately as "(N paused)".
  const appliedNegativesCount = activeKeywords.length + (inheritedNegatives?.length ?? 0);

  // Merge the ad group's own negatives (deletable) with inherited campaign / shared-list negatives
  // (read-only). Sorting/pagination treat them as one list; the Source pill keeps origins clear.
  const rows: NegativeRow[] = [
    ...negativeKeywords.map((nk): NegativeRow => ({ ...nk, sourceLabel: 'Ad', sourceType: 'own', deletable: true })),
    ...(inheritedNegatives || []).map((n): NegativeRow => ({
      keyword: n.keyword,
      match_type: n.match_type,
      sourceLabel: n.source_label,
      sourceType: n.source,
      deletable: false,
    })),
  ];

  const handleRemoveKeyword = (keyword: NegativeKeywordData) => {
    setDeleteTarget(keyword);
  };

  const handleConfirmDelete = () => {
    if (deleteTarget && onRemove) {
      onRemove(deleteTarget);
    }
    setDeleteTarget(null);
  };

  // Inline match-type change for the ad group's own negatives. Negatives carry no performance
  // history, so unlike positive keywords this commits straight to the draft without a confirm
  // (the change stays reversible until the ad is saved). #5555
  const handleMatchTypeChange = (keyword: NegativeKeywordData, newMatchType: MatchType) => {
    if ((keyword.match_type || 'EXACT').toUpperCase() === newMatchType) return;
    // Skip if the same text already exists with the target match type (mirrors the add flow's dedup).
    const text = getKeywordText(keyword).toLowerCase();
    const isDuplicate = negativeKeywords.some(
      (k) => getKeywordText(k).toLowerCase() === text && (k.match_type || 'EXACT').toUpperCase() === newMatchType
    );
    if (isDuplicate) return;
    onMatchTypeChange?.(keyword, newMatchType);
  };

  // Define columns inside the component for access to `disabled` and `handleRemoveKeyword`
  const columns: ColumnDef<NegativeRow, unknown>[] = [
    {
      id: 'keyword',
      accessorFn: (row) => getKeywordText(row),
      header: 'Keyword',
      cell: ({ row }) => (
        <div className='flex items-center gap-2'>
          <span className={`text-sm font-medium ${row.original.is_paused ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
            {getKeywordText(row.original)}
          </span>
          {row.original.is_paused && (
            <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500'>
              Paused
            </span>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'match_type',
      header: 'Match Type',
      cell: ({ row }) => {
        // The ad group's own (non-paused) negatives get the inline editable pill. Inherited and
        // paused rows, and the whole-component read-only mode, stay as static pills.
        const editable = !disabled && row.original.deletable && !row.original.is_paused && !!onMatchTypeChange;
        if (editable) {
          return (
            <MatchTypeSelect
              matchType={row.original.match_type}
              onChange={(mt) => handleMatchTypeChange(row.original, mt as MatchType)}
              pillClasses={getMatchTypePillStyle}
              matchTypes={MATCH_TYPES}
            />
          );
        }
        // Inherited and paused rows, and the whole-component read-only mode, render the same colored
        // pill without the dropdown affordance.
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${row.original.is_paused ? 'bg-gray-50 text-gray-400' : getMatchTypePillStyle(row.original.match_type || 'EXACT')}`}>
            {formatMatchType(row.original.match_type || 'EXACT')}
          </span>
        );
      },
    },
    {
      accessorKey: 'sourceLabel',
      header: 'Source',
      cell: ({ row }) => {
        // Campaign-inherited negatives are managed on the campaign's own Negatives tab, so make
        // their Source pill a cobalt link that jumps there (#5557). Shared-list rows have no
        // destination route yet, so they keep the static pill.
        const campaignSlug = campaign?.slug;
        if (row.original.sourceType === 'campaign' && campaignSlug) {
          return (
            <button
              type='button'
              onClick={() =>
                navigate({
                  to: '/campaigns/google/$campaignslug',
                  params: { campaignslug: campaignSlug },
                  hash: 'negatives',
                })
              }
              title='View on the campaign Negatives tab'
              className='inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#EDE9FE] text-[#290BD9] hover:bg-[#DDD6FE] transition-colors'
            >
              {row.original.sourceLabel}
              <ChevronRight className='h-3 w-3' />
            </button>
          );
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSourcePillStyle(row.original.sourceType)}`}>
            {row.original.sourceLabel}
          </span>
        );
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Date Added',
      cell: ({ row }) => (
        <span className={`text-sm ${row.original.is_paused ? 'text-gray-400' : 'text-gray-500'}`}>
          {formatDate(row.original.created_at)}
        </span>
      ),
    },
    {
      id: 'reason',
      header: 'Reason',
      enableSorting: false,
      cell: () => (
        <span className='text-sm text-gray-500'>-</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <div className='text-right'>
          {!disabled && row.original.deletable && !row.original.is_paused && (
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50'
              onClick={() => handleRemoveKeyword(row.original)}
            >
              <Trash2 className='h-4 w-4' />
              <span className='sr-only'>Remove {getKeywordText(row.original)}</span>
            </Button>
          )}
        </div>
      ),
    },
  ];

  const resetForm = () => {
    setBulkKeywords('');
    setMatchType('PHRASE');
    setBulkKeywordsError(undefined);
    setIsModalOpen(false);
  };

  const handleAddKeywords = () => {
    if (!selectedAd?.id) return;

    const keywordLines = bulkKeywords
      .split('\n')
      .map((line) => line.trim().toLowerCase())
      .filter((line) => line.length > 0);

    if (keywordLines.length === 0) {
      setBulkKeywordsError('Required');
      return;
    }

    const valid = keywordLines.filter(
      (text) => !activeKeywords.some((k) => getKeywordText(k).toLowerCase() === text && k.match_type === matchType)
    );

    if (valid.length === 0) {
      // The live dupe banner above the button lists the skipped keywords.
      setBulkKeywordsError('All keywords are already added');
      return;
    }

    const newKeywords: NegativeKeywordData[] = valid.map((kw) => ({
      keyword: kw,
      match_type: matchType,
    }));

    if (onAdd) {
      onAdd(newKeywords);
    }
    resetForm();
  };

  if (!selectedAd) {
    return (
      <div className='space-y-6'>
        <div className='flex flex-col items-center justify-center py-16 text-center'>
          <Ban className='h-12 w-12 text-gray-500 mb-4' strokeWidth={1} />
          <h3 className='text-lg font-semibold text-gray-500 mb-2'>Select an Ad</h3>
          <p className='text-gray-500'>Select an ad from the sidebar to view and manage its negative keywords.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className='flex items-center justify-between mb-4'>
        <div>
          <div className='flex items-center gap-2'>
            <h2 className='text-xl font-semibold text-gray-900'>Ad Negative Keywords</h2>
            <StringBadge string={String(appliedNegativesCount)} size='sm' />
            {pausedKeywords.length > 0 && (
              <span className='text-xs text-gray-500'>({pausedKeywords.length} paused)</span>
            )}
          </div>
          <p className='text-sm text-gray-500 mt-1'>Keywords you don't want this ad to show for.</p>
        </div>
        {!hideAddButton && (
          <Button
            variant='outline'
            className='text-gray-900'
            onClick={() => { setBulkKeywordsError(undefined); setIsModalOpen(true); }}
            disabled={disabled}
          >
            <Plus className='h-4 w-4 mr-2' />
            Add Negatives
          </Button>
        )}
      </div>

      {/* Negative Keywords Table */}
      <DataTable
        data={rows}
        columns={columns}
        variant='flush'
        pageSize={999}
        initialSorting={[{ id: 'sourceLabel', desc: false }]}
        emptyIcon={Ban}
        emptyTitle='No Negative Keywords'
        emptyDescription='Add negative keywords to prevent this ad from showing for specific search terms.'
      />

      {/* Add Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className='sm:max-w-[500px]' aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add Negative Keywords</DialogTitle>
          </DialogHeader>

          <div className='py-4 space-y-4'>
            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <Label>Keywords (one per line)</Label>
                <FieldError message={bulkKeywordsError} />
              </div>
              <Textarea
                value={bulkKeywords}
                onChange={(e) => { setBulkKeywords(e.target.value); setBulkKeywordsError(undefined); }}
                placeholder={'Enter keywords, one per line\nfree\ndownload\ncheap'}
                className='min-h-[150px] text-gray-900'
                error={!!bulkKeywordsError}
              />
            </div>
            <div className='space-y-2'>
              <Label>Match Type</Label>
              <Select
                value={matchType}
                // Match type feeds the duplicate check, so changing it clears the inline error too.
                onValueChange={(value) => { setMatchType(value as MatchType); setBulkKeywordsError(undefined); }}
              >
                <SelectTrigger className='w-[200px]'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MATCH_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {formatMatchType(type)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(() => {
              const lines = bulkKeywords
                .split('\n')
                .map((line) => line.trim().toLowerCase())
                .filter((line) => line.length > 0);
              if (lines.length === 0) return null;
              const dupes = lines.filter((text) =>
                activeKeywords.some((k) => getKeywordText(k).toLowerCase() === text && k.match_type === matchType)
              );
              if (dupes.length === 0) return null;
              return (
                <div className='flex items-start gap-2 px-3 py-2 rounded-lg border-l-4 bg-[#FEF3C7] border-[#FDE68A] text-[#A16207] text-sm'>
                  <AlertCircle className='h-4 w-4 mt-0.5 shrink-0' />
                  <span>{dupes.length} keyword{dupes.length === 1 ? '' : 's'} skipped: {dupes.length === 1 ? 'it' : 'they'} already exist{dupes.length === 1 ? 's' : ''} with match type {matchType.toLowerCase()}.</span>
                </div>
              );
            })()}
          </div>

          <div className='flex justify-end gap-2'>
            <Button variant='outline' onClick={resetForm}>
              Cancel
            </Button>
            <Button
              onClick={handleAddKeywords}
              // Tell-on-click (#5857): Add stays enabled; empty/all-duplicate input
              // surfaces inline at the field on click.
              className='bg-[#290BD9] hover:bg-[#2309C5]'
            >
              Add
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <ConformationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title='Delete Negative Keyword'
        description={`Are you sure you want to delete "${deleteTarget ? getKeywordText(deleteTarget) : ''}"? This action cannot be undone.`}
      />
    </div>
  );
}
